// styles
import './styles/graphic.scss';
import { select } from 'd3-selection';

// Format number with K abbreviation
const formatK = (value, decimals = 0) => {
  const k = value / 1000;
  if (decimals > 0) {
    const formatted = k.toFixed(decimals);
    // Remove trailing .0 if present
    return formatted.endsWith('.0')
      ? `${formatted.slice(0, -2)}K`
      : `${formatted}K`;
  }
  return `${Math.round(k)}K`;
};

export default function (props, container, isMobile) {
  const {
    color,
    labelColor,
    data,
    fmtYearAbbrev,
    fmtYearFull,
    fmtMonth,
    fmtMonthDay,
    height,
    initialWidth,
    initialHeight,
    margins,
    path,
    width,
    x,
    xAxis,
    y,
    yAxis,
  } = props;

  const xTicks = isMobile ? 5 : 10;
  const yTicks = 4; // Fixed tick count for ~10K intervals

  const xGrid = xAxis(x)
    .ticks(xTicks)
    .tickFormat(d => (isMobile
      ? `\u2019${fmtYearAbbrev(d)}`
      : fmtYearFull(d)));

  // legend
  // container.insert('ul', ':first-child')
  //   .attr('class', 'key')
  //   .selectAll('li')
  //   .data(data)
  //   .enter()
  //   .append('li')
  //   .attr('class', 'key-item')
  //   .html(d => `<span class='key-block' style='background-color:${color(d.id)};'></span> ${d.id.toUpperCase()}`);

  // svg
  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', initialHeight);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  // x axis
  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, height]})`)
    .call(xGrid);

 	// y axis
  const yGrid = g.append('g')
    .attr('class', 'grid y')
    .call(yAxis(y).ticks(yTicks));

  // Format y-axis labels with K abbreviation
  yGrid.selectAll('text')
    .text(d => formatK(d));

  yGrid.selectAll('line').attr('x2', width);

  // Presidential administration ranges
  const bidenStart = new Date('2021-01-20');
  const trump2Start = new Date('2025-01-20');
  const [dataStart, dataEnd] = x.domain();

  if (dataStart < bidenStart) {
    const trump1End = bidenStart < dataEnd ? bidenStart : dataEnd;
    g.append('rect')
      .attr('class', 'admin-range trump')
      .attr('x', x(dataStart))
      .attr('y', 0)
      .attr('width', x(trump1End) - x(dataStart))
      .attr('height', height)
      .attr('fill', '#e0e0e0')
      .attr('opacity', 0.3);
    const trump1MidX = (x(dataStart) + x(trump1End)) / 2;
    g.append('text')
      .attr('class', 'admin-label')
      .attr('x', trump1MidX)
      .attr('y', -10)
      .attr('text-anchor', 'middle')
      .text('Trump');
  }

  if (dataStart < trump2Start && dataEnd > bidenStart) {
    const bidenStartClamped = bidenStart > dataStart ? bidenStart : dataStart;
    const bidenEndClamped = trump2Start < dataEnd ? trump2Start : dataEnd;
    const bidenMidX = (x(bidenStartClamped) + x(bidenEndClamped)) / 2;
    g.append('text')
      .attr('class', 'admin-label')
      .attr('x', bidenMidX)
      .attr('y', -10)
      .attr('text-anchor', 'middle')
      .text('Biden');
  }

  if (dataEnd > trump2Start) {
    const trump2StartClamped = trump2Start > dataStart ? trump2Start : dataStart;
    g.append('rect')
      .attr('class', 'admin-range trump')
      .attr('x', x(trump2StartClamped))
      .attr('y', 0)
      .attr('width', x(dataEnd) - x(trump2StartClamped))
      .attr('height', height)
      .attr('fill', '#e0e0e0')
      .attr('opacity', 0.3);
    const trump2MidX = (x(trump2StartClamped) + x(dataEnd)) / 2;
    g.append('text')
      .attr('class', 'admin-label')
      .attr('x', trump2MidX)
      .attr('y', -10)
      .attr('text-anchor', 'middle')
      .text('Trump');
  }

  // lines
  const lines = g.append('g')
    .attr('class', 'lines')
    .selectAll('g')
    .data(data)
    .enter()
    .append('g')
    .attr('class', 'line');

  lines.append('path')
    .attr('stroke', d => color(d.id))
    .attr('d', d => path(d.values));

  // labels
  const label = lines.append('g')
    .attr('class', 'data-label');

  // Calculate initial y-positions for collision detection
  const minSpacing = 30; // Minimum pixels between labels (based on 14px font)
  const labelPositions = data.map((d) => {
    const d0 = d.values[d.values.length - 1];
    return {
      id: d.id,
      x: x(d0.date) + 5,
      y: y(d0.value) + 3,
      originalY: y(d0.value) + 3,
    };
  });

  // Sort by y position and adjust overlapping labels
  labelPositions.sort((a, b) => a.y - b.y);
  for (let i = 1; i < labelPositions.length; i++) {
    const prev = labelPositions[i - 1];
    const curr = labelPositions[i];
    if (curr.y - prev.y < minSpacing) {
      curr.y = prev.y + minSpacing;
    }
  }

  // Create a lookup map for adjusted positions
  const positionMap = new Map(labelPositions.map(p => [p.id, p]));

  // Single shared date label above the topmost value label
  const topPos = labelPositions[0];
  const sharedDate = data[0].values[data[0].values.length - 1].date;
  g.append('text')
    .attr('class', 'date-label')
    .attr('font-size', '12px')
    .attr('fill', '#5E5E5E')
    .attr('transform', `translate(${[topPos.x, topPos.y - 20]})`)
    .text(fmtMonthDay(sharedDate));

  label.append('text')
    .attr('fill', d => labelColor(d.id))
    .attr('transform', (d) => {
      const pos = positionMap.get(d.id);
      return `translate(${[pos.x, pos.y]})`;
    })
    .each(function (d) {
      const d0 = d.values[d.values.length - 1];
      const sel = select(this);
      sel.append('tspan')
        .attr('x', 0)
        .attr('dy', 0)
        .text(formatK(d0.value, 1));
    });
}
